import React, { useState } from 'react';
import { ArrowRight, ArrowLeftRight, MapPin, Navigation, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const drops = [
  {
    id: 'hyderabad',
    city: 'Hyderabad',
    from: 'Bengaluru',
    distance: '~570 km',
    duration: '9-10 hours',
    sedan: 12999,
    suv: 15999,
    image: '/hyderabad.jpeg',
  },
  {
    id: 'mysuru',
    city: 'Mysuru',
    from: 'Bengaluru',
    distance: '~145 km',
    duration: '3-3.5 hours',
    sedan: 3999,
    suv: 5999,
    image: '/route-ooty.jpeg',
  },
  {
    id: 'chennai',
    city: 'Chennai',
    from: 'Bengaluru',
    distance: '~350 km',
    duration: '6-7 hours',
    sedan: 7999,
    suv: 9999,
    image: '/chennai.jpeg',
  },
  {
    id: 'coimbatore',
    city: 'Coimbatore',
    from: 'Bengaluru',
    distance: '~365 km',
    duration: '6-7 hours',
    sedan: 5999,
    suv: 7999,
    image: '/coimbatore.jpeg',
  },
  {
    id: 'tirupati',
    city: 'Tirupati',
    from: 'Bengaluru',
    distance: '~250 km',
    duration: '5-6 hours',
    sedan: 7999,
    suv: 9999,
    image: '/tirupati.jpeg',
  },
  {
    id: 'goa',
    city: 'Goa',
    from: 'Bengaluru',
    distance: '~560 km',
    duration: '10-11 hours',
    sedan: 13999,
    suv: 15999,
    image: '/goa.jpeg',
  },
];

const formatInr = (n) => `₹${n.toLocaleString('en-IN')}`;

const DropCard = ({ drop, reverse, onBook }) => {
  const origin = reverse ? drop.city : drop.from;
  const destination = reverse ? drop.from : drop.city;
  return (
  <Card data-testid={`intercity-card-${drop.id}`} className="group lux-card rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
    <div className="relative h-52 overflow-hidden flex-shrink-0">
      <img src={drop.image} alt={`${origin} to ${destination}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
      <div className="absolute top-4 left-4">
        <span className="inline-flex items-center px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-[#d4af37]/40 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.15em]">
          One Way Drop
        </span>
      </div>
      <div className="absolute bottom-4 left-4 right-4">
        <div data-testid={`intercity-${drop.id}-route`} className="flex items-center gap-2 text-white/80 text-sm mb-1">
          <MapPin className="w-4 h-4 text-[#d4af37]" aria-hidden="true" />
          {origin}
          <ArrowRight className="w-4 h-4 text-[#d4af37]" aria-hidden="true" />
        </div>
        <h3 className="font-display text-2xl font-semibold text-white">{destination}</h3>
        <div className="flex items-center gap-4 text-sm text-white/80 mt-1">
          <span className="flex items-center"><Navigation className="w-4 h-4 mr-1" aria-hidden="true" />{drop.distance}</span>
          <span className="flex items-center"><Clock className="w-4 h-4 mr-1" aria-hidden="true" />{drop.duration}</span>
        </div>
      </div>
    </div>
    <CardContent className="p-6 flex flex-col flex-1">
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="rounded-xl bg-[#1a1a1a] border border-white/10 p-4">
          <p className="text-xs uppercase tracking-[0.15em] text-gray-400 mb-1">Sedan</p>
          <p data-testid={`intercity-${drop.id}-sedan-price`} className="text-2xl font-bold text-[#d4af37]">{formatInr(drop.sedan)}</p>
          <p className="text-xs text-gray-500 mt-1">Dzire / Etios</p>
        </div>
        <div className="rounded-xl bg-[#1a1a1a] border border-[#d4af37]/30 p-4">
          <p className="text-xs uppercase tracking-[0.15em] text-gray-400 mb-1">SUV</p>
          <p data-testid={`intercity-${drop.id}-suv-price`} className="text-2xl font-bold text-[#d4af37]">{formatInr(drop.suv)}</p>
          <p className="text-xs text-gray-500 mt-1">Innova Crysta / Ertiga</p>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-4">All inclusive — driver allowance, fuel, tolls &amp; state permit included.</p>
      <Button data-testid={`intercity-book-${drop.id}`} onClick={onBook} className="w-full mt-auto rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold transition-colors">
        Book {origin} → {destination}
      </Button>
    </CardContent>
  </Card>
  );
};

export const IntercityDrops = () => {
  const [reverse, setReverse] = useState(false);
  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleClass = (active) =>
    `px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
      active ? 'bg-[#d4af37] text-black' : 'text-[#d4af37] hover:bg-[#d4af37]/10'
    }`;

  return (
    <section id="intercity" data-testid="intercity-section" className="py-24 bg-[#000000]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em] mb-4">
            Fixed Fares
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-4 tracking-tight">
            One Way Intercity Drops
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Pay only for one way. Same flat, all-inclusive fare in both directions.
          </p>
        </div>

        <div className="flex justify-center mb-12">
          <div data-testid="intercity-direction-toggle" className="inline-flex items-center gap-1 p-1 rounded-full bg-[#121212] border border-white/10">
            <button data-testid="intercity-from-bengaluru" onClick={() => setReverse(false)} className={toggleClass(!reverse)}>
              From Bengaluru
            </button>
            <ArrowLeftRight className="w-4 h-4 text-[#d4af37]/60" aria-hidden="true" />
            <button data-testid="intercity-to-bengaluru" onClick={() => setReverse(true)} className={toggleClass(reverse)}>
              To Bengaluru
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {drops.map((drop) => (
            <DropCard key={drop.id} drop={drop} reverse={reverse} onBook={scrollToBooking} />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-4">Need a one way drop to another city?</p>
          <a href="tel:+917507867648">
            <button data-testid="intercity-custom-cta" className="text-[#d4af37] font-semibold hover:underline">
              Call us for a fixed quote →
            </button>
          </a>
        </div>
      </div>
    </section>
  );
};
